import { useCallback, useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import clsx from "clsx";

type ImageCarouselProps = {
  imageUrls: string[];
  fallbackUrl?: string;
  alt: string;
  className?: string;
};

const DEFAULT_FALLBACK = "/images/default-plan-banner.png";
const DRAG_THRESHOLD = 5;

export function ImageCarousel({
  imageUrls,
  fallbackUrl = DEFAULT_FALLBACK,
  alt,
  className,
}: ImageCarouselProps) {
  const images = imageUrls.length > 0 ? imageUrls : [fallbackUrl];
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{
    startX: number;
    startScroll: number;
    pointerId: number;
    moved: boolean;
  } | null>(null);
  const movedRef = useRef(false);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    let frame = 0;
    const onScroll = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const w = scroller.clientWidth;
        if (w === 0) return;
        setSelectedIndex(Math.round(scroller.scrollLeft / w));
      });
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const dx = e.clientX - drag.startX;
      if (!drag.moved && Math.abs(dx) > DRAG_THRESHOLD) drag.moved = true;
      scroller.scrollLeft = drag.startScroll - dx;
    };

    const end = () => {
      const scroller = scrollerRef.current;
      movedRef.current = dragRef.current?.moved ?? false;
      dragRef.current = null;
      setIsDragging(false);
      if (!scroller) return;
      const w = scroller.clientWidth;
      if (w === 0) return;
      const idx = Math.round(scroller.scrollLeft / w);
      scroller.scrollTo({ left: w * idx, behavior: "smooth" });
    };

    const onUp = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      end();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    window.addEventListener("blur", end);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      window.removeEventListener("blur", end);
    };
  }, [isDragging]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse") return;
    if (images.length < 2) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    dragRef.current = {
      startX: e.clientX,
      startScroll: scroller.scrollLeft,
      pointerId: e.pointerId,
      moved: false,
    };
    setIsDragging(true);
  };

  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (movedRef.current) {
      movedRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const scrollTo = useCallback((i: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollTo({ left: scroller.clientWidth * i, behavior: "smooth" });
  }, []);

  return (
    <div
      className={clsx(
        "relative w-full aspect-[434/372] overflow-hidden rounded-lg select-none",
        className,
      )}
    >
      <div
        ref={scrollerRef}
        onPointerDown={handlePointerDown}
        onClickCapture={handleClickCapture}
        className={clsx(
          "flex h-full overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          isDragging ? "cursor-grabbing" : "snap-x snap-mandatory",
          !isDragging && images.length > 1 && "cursor-grab",
        )}
      >
        {images.map((url, i) => (
          <div
            key={i}
            className="snap-start flex-shrink-0 w-full h-full bg-butter-cream bg-cover bg-center"
            style={{ backgroundImage: `url(${url})` }}
            role="img"
            aria-label={`${alt} — image ${i + 1} of ${images.length}`}
          />
        ))}
      </div>

      {images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous image"
            onClick={() => scrollTo(Math.max(0, selectedIndex - 1))}
            disabled={selectedIndex === 0}
            className="hidden lg:flex absolute left-2 top-1/2 -translate-y-1/2 items-center justify-center text-butter-cream-light transition-opacity hover:opacity-80 disabled:opacity-0 disabled:pointer-events-none"
          >
            <FiChevronLeft size={32} strokeWidth={2.5} aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Next image"
            onClick={() =>
              scrollTo(Math.min(images.length - 1, selectedIndex + 1))
            }
            disabled={selectedIndex === images.length - 1}
            className="hidden lg:flex absolute right-2 top-1/2 -translate-y-1/2 items-center justify-center text-butter-cream-light transition-opacity hover:opacity-80 disabled:opacity-0 disabled:pointer-events-none"
          >
            <FiChevronRight size={32} strokeWidth={2.5} aria-hidden />
          </button>
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to image ${i + 1}`}
                onClick={() => scrollTo(i)}
                className={clsx(
                  "w-2 h-2 rounded-full transition-colors",
                  i === selectedIndex
                    ? "bg-butter-cream-light"
                    : "bg-butter-cream-light/40 hover:bg-butter-cream-light/70",
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
